import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  listScheduleTypes,
  listCalendar,
  createCalendarEntries,
  deleteCalendarEntry,
  getTodaySchedule,
} from "../../../api/schoolConfigApi";
import { showSuccess, showError } from "../../../utils/notifications";
import { getContextualError, logError } from "../../../utils/errorHandling";
import CollapsibleSection from "../../common/CollapsibleSection";
import SearchFilter from "../../common/SearchFilter";
import PaginationControls from "../../common/PaginationControls";
import CsvFormatExplainer from "../CsvFormatExplainer";
import ScheduleVariantModal from "../ScheduleVariantModal";
import CalendarEntryEditModal from "../CalendarEntryEditModal";
import { useLoadingStates } from "../../../hooks/useLoadingStates";
import { parseCsv, escapeCsv, readFileAsText } from "../../../utils/csvParsing";
import { formatCalendarDate } from "../../../utils/calendarDate";
import { isSplitVariant } from "../../../utils/scheduleVariant";
import styles from "./ScheduleConfigPanel.module.css";
import adminStyles from "../AdminPanel.module.css";
import type { components } from "../../../api/types.gen";

type ScheduleTypeResponse = components["schemas"]["ScheduleTypeResponse"];
// The generated ScheduleTypeResponse omits `abDay`, but
// GET /schools/:id/schedule-types genuinely returns it (see
// apps/schools-api/src/routes/scheduleType.ts's toScheduleTypeResponse) —
// intersect the field back in until types.gen.ts is regenerated with it.
export type ScheduleType = ScheduleTypeResponse & { abDay?: "A" | "B" | null };
type CalendarEntry = components["schemas"]["CalendarEntry"];
type SchoolCalendarResponse = components["schemas"]["SchoolCalendarResponse"];
type ScheduleTodayResponse = components["schemas"]["ScheduleTodayResponse"];

/**
 * Optional {schoolType, totalPeriods} config still sourced from Firestore by
 * whichever parent renders this panel and handed straight to
 * ScheduleVariantModal (see this file's SCOPE NOTE header comment).
 */
export interface SchoolConfig {
  schoolType?: string;
  totalPeriods?: number;
}

/**
 * One row of the "variant" concept the admin UI edits — see
 * groupScheduleTypes. Exported so a parent (AdminPanel) can consume the same
 * grouping shape.
 */
export interface ScheduleVariant {
  variantName: string;
  scheduleTypeIds: {
    single: number | null;
    aDay: number | null;
    bDay: number | null;
  };
}

export interface ScheduleConfigPanelProps {
  schoolId?: number | string;
  schoolConfig?: SchoolConfig;
}

type VariantModalMode = "create" | "edit" | "duplicate";

/** Reports whether an in-flight fetch has been superseded/cancelled. */
type IsCancelled = () => boolean;

/** Result of client-side CSV validation before a bulk calendar upsert. */
interface ParsedCalendarCsv {
  errors: string[];
  entries: CalendarEntry[];
}

// Ported from AdminPanel.js's "Calendar & Schedule Management" (JSX lines
// ~4658-4856, handlers addCalendarEntry/removeCalendarEntry/
// addScheduleOverride/removeScheduleOverride, lines ~1141-1289) and
// "Schedule Variants Management" (JSX ~4858-4958, loadScheduleVariants
// ~531-573) sections, as part of the Firebase -> hallpass-backend
// migration (Admin step 5 — the largest data-model translation in the
// whole migration; see ScheduleVariantModal.js's header comment for the
// full writeup of the legacy-vs-new model mismatch).
//
// Three Firebase callables are eliminated here: `manageCalendar`,
// `manageScheduleOverrides`, and `manageScheduleVariants`. All three are
// replaced by direct calls to schoolConfigApi's schedule-type/period/
// calendar endpoints, following the same GET-on-mount +
// refetch-after-mutation pattern as DestinationsPanel.js (no real-time
// listeners for this admin config data).
//
// UNIFICATION: legacy tracked "A/B day calendar" (a bare abDay marker) and
// "schedule overrides" (a variant-name override) as two separate Firestore
// collections/lists. The new backend has one `SchoolCalendar` row per date
// (schoolId + date, upsert on conflict) referencing a single scheduleTypeId
// — since the REQUIRED A/B-DAY STRATEGY represents "A Day"/"B Day"
// themselves as schedule types, that one reference covers both legacy
// concepts at once. This panel therefore has ONE calendar list/form, not
// two.
//
// SCOPE NOTE: `schoolConfig` ({schoolType, totalPeriods}) is still sourced
// from Firestore by whichever parent renders this panel (AdminPanel.js
// today) — migrating that read is a separate, already-tracked concern, not
// one of the three callables this step removes. It's accepted here as an
// optional pass-through prop, exactly as ScheduleVariantModal.js already
// did, and handed straight to the modal.

const CALENDAR_PAGE_SIZE = 20;

// Groups the flat schedule-type list into the "variant" concept the admin
// UI edits: two schedule types sharing a `name` with `abDay` 'A'/'B' are the
// two halves of a split (Block/Mixed school) variant; a schedule type with
// abDay: null is its own single-schedule-type variant. See
// ScheduleVariantModal.js's header comment for the full A/B-day strategy.
// Exported so AdminPanel.js can reuse this exact grouping to reconstruct
// schoolConfig from the same data (see its "Derive schoolConfig" effect) —
// keeps the split-detection logic in one place rather than a second
// heuristic that could disagree with this one.
export function groupScheduleTypes(
  scheduleTypes: ScheduleType[],
): ScheduleVariant[] {
  const groups = new Map<string, ScheduleVariant>();

  for (const st of scheduleTypes) {
    const base = st.name;
    const slot: "aDay" | "bDay" | "single" =
      st.abDay === "A" ? "aDay" : st.abDay === "B" ? "bDay" : "single";

    let group = groups.get(base);
    if (!group) {
      group = {
        variantName: base,
        scheduleTypeIds: { single: null, aDay: null, bDay: null },
      };
      groups.set(base, group);
    }
    group.scheduleTypeIds[slot] = st.id;
  }

  return [...groups.values()].sort((a, b) =>
    a.variantName.localeCompare(b.variantName),
  );
}

// Sample CSV shown alongside the "Download Sample CSV" button — built from
// this school's own schedule types so the header/example matches what the
// admin will actually type. Mirrors ClassSectionsPanel.js's
// buildSampleBulkCsv/downloadSampleBulkCsv convention. One row illustrates
// a Block variant's A/B pair (if one exists) and one illustrates a plain
// variant with A/B left blank; falls back to a generic "Regular" example
// row when the school has no schedule types configured yet.
function buildSampleCalendarCsv(scheduleTypes: ScheduleType[]): string {
  const header = "Date,Schedule Type,A/B";
  const blockType = scheduleTypes.find((st) => st.abDay);
  const singleType = scheduleTypes.find((st) => !st.abDay);

  const rows = [];
  if (blockType) {
    rows.push(
      `2026-08-24,${escapeCsv(blockType.name)},${escapeCsv(blockType.abDay)}`,
    );
  }
  if (singleType) {
    rows.push(`2026-08-25,${escapeCsv(singleType.name)},`);
  }
  if (rows.length === 0) {
    rows.push("2026-08-24,Regular,");
  }

  return [header, ...rows].join("\n");
}

// Client-side only, no network calls — collects ALL row errors up front so
// nothing is submitted unless the entire file resolves cleanly, mirroring
// the bulk calendar endpoint's own atomicity (POST /schools/:id/calendar
// rejects the whole batch on any invalid scheduleTypeId — see
// apps/schools-api/src/routes/calendar.ts). Each row's "Schedule
// Type"/"A/B" pair is resolved against the already-loaded scheduleTypes
// list — blank A/B resolves to abDay: null, so a non-block school's rows
// (which never set the A/B column) are unaffected by that column at all.
// Dates not present in the file are left untouched: the bulk endpoint
// upserts on (schoolId, date), so this is a pure additive/overwrite-only
// submission, never a range-replace.
function parseCalendarCsv(
  csvText: string,
  scheduleTypes: ScheduleType[],
): ParsedCalendarCsv {
  const { headers, dataRows } = parseCsv(csvText);

  if (dataRows.length === 0) {
    return {
      errors: ["File must contain a header row and at least one data row."],
      entries: [],
    };
  }

  const dateIndex = headers.indexOf("date");
  const nameIndex = headers.indexOf("schedule type");
  const abDayIndex = headers.indexOf("a/b");
  const missing = [];
  if (dateIndex === -1) missing.push("Date");
  if (nameIndex === -1) missing.push("Schedule Type");
  if (abDayIndex === -1) missing.push("A/B");
  if (missing.length > 0) {
    return {
      errors: [
        `Missing required column(s): ${missing.join(", ")}. Header should be: Date,Schedule Type,A/B`,
      ],
      entries: [],
    };
  }

  const typeByKey = new Map(
    scheduleTypes.map((st): [string, ScheduleType] => [
      `${st.name.toLowerCase()}|${(st.abDay || "").toUpperCase()}`,
      st,
    ]),
  );

  const errors: string[] = [];
  const entries: CalendarEntry[] = [];
  const seenDates = new Set<string>();

  dataRows.forEach((values, i) => {
    const rowNum = i + 2;

    if (values.length !== headers.length) {
      errors.push(
        `Row ${rowNum}: Expected ${headers.length} columns but found ${values.length}. Check for missing commas or extra commas.`,
      );
      return;
    }

    const date = values[dateIndex].trim();
    const name = values[nameIndex].trim();
    const abDayRaw = values[abDayIndex].trim();
    const abDay = abDayRaw.toUpperCase();

    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      errors.push(
        `Row ${rowNum}: "${date}" is not a valid date (expected YYYY-MM-DD).`,
      );
      return;
    }
    if (seenDates.has(date)) {
      errors.push(
        `Row ${rowNum}: duplicate date "${date}" (also listed earlier in this file).`,
      );
      return;
    }
    if (abDay !== "" && abDay !== "A" && abDay !== "B") {
      errors.push(
        `Row ${rowNum} (${date}): A/B must be "A", "B", or blank — got "${abDayRaw}".`,
      );
      return;
    }
    if (!name) {
      errors.push(`Row ${rowNum} (${date}): Schedule Type is required.`);
      return;
    }

    const scheduleType = typeByKey.get(`${name.toLowerCase()}|${abDay}`);
    if (!scheduleType) {
      errors.push(
        `Row ${rowNum} (${date}): no schedule type named "${name}"${
          abDay ? ` with A/B "${abDay}"` : ""
        }.`,
      );
      return;
    }

    seenDates.add(date);
    entries.push({ date, scheduleTypeId: scheduleType.id, note: null });
  });

  return { errors, entries };
}

const ScheduleConfigPanel = ({
  schoolId: schoolIdProp,
  schoolConfig,
}: ScheduleConfigPanelProps = {}): React.ReactElement => {
  const { currentUser } = useAuth();
  const schoolId = schoolIdProp ?? currentUser?.schoolId;

  const [loadingStates, setLoading] = useLoadingStates({
    loadScheduleTypes: false,
    loadCalendar: false,
    loadToday: false,
    addCalendar: false,
    removeCalendar: false,
    uploadCalendarCsv: false,
  });

  // Schedule types / variants
  const [scheduleTypes, setScheduleTypes] = useState<ScheduleType[]>([]);
  const variantGroups = useMemo(
    () => groupScheduleTypes(scheduleTypes),
    [scheduleTypes],
  );

  // Guards against a stale response clobbering `scheduleTypes` for the
  // wrong schoolId: if schoolId changes while a fetch is in flight, the
  // effect below flips `cancelled` on cleanup so an earlier request that
  // resolves after a later one is dropped instead of overwriting state.
  // Mirrors the cancelled-flag pattern in useAdminSetupChecklist.js's
  // fetchCounts.
  const fetchScheduleTypes = useCallback(
    async (isCancelled: IsCancelled = () => false) => {
      if (!schoolId) return;
      if (!isCancelled()) setLoading("loadScheduleTypes", true);
      try {
        const res = await listScheduleTypes(schoolId);
        if (!isCancelled()) setScheduleTypes(res.data);
      } catch (error) {
        logError(error, "loadScheduleTypes");
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              "Failed to load schedule types. Please try again.",
            ),
          );
          setScheduleTypes([]);
        }
      } finally {
        if (!isCancelled()) setLoading("loadScheduleTypes", false);
      }
    },
    [schoolId, setLoading],
  );

  useEffect(() => {
    let cancelled = false;
    fetchScheduleTypes(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchScheduleTypes]);

  // Variant modal
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [variantModalMode, setVariantModalMode] =
    useState<VariantModalMode>("create");
  const [selectedVariant, setSelectedVariant] = useState<ScheduleVariant | null>(
    null,
  );

  const handleCreateVariant = (): void => {
    setSelectedVariant(null);
    setVariantModalMode("create");
    setShowVariantModal(true);
  };

  const handleEditVariant = (variant: ScheduleVariant): void => {
    setSelectedVariant(variant);
    setVariantModalMode("edit");
    setShowVariantModal(true);
  };

  const handleDuplicateVariant = (variant: ScheduleVariant): void => {
    setSelectedVariant(variant);
    setVariantModalMode("duplicate");
    setShowVariantModal(true);
  };

  const handleVariantSaved = (): void => {
    fetchScheduleTypes();
  };

  // Calendar
  const [calendarEntries, setCalendarEntries] = useState<
    SchoolCalendarResponse[]
  >([]);
  const [calendarFilter, setCalendarFilter] = useState("");
  const [calendarCurrentPage, setCalendarCurrentPage] = useState(0);

  useEffect(() => {
    setCalendarCurrentPage(0);
  }, [calendarFilter]);

  // Guards against a stale response clobbering `calendarEntries` for the
  // wrong schoolId — same cancelled-flag pattern as fetchScheduleTypes
  // above.
  const fetchCalendar = useCallback(
    async (isCancelled: IsCancelled = () => false) => {
      if (!schoolId) return;
      if (!isCancelled()) setLoading("loadCalendar", true);
      try {
        const res = await listCalendar(schoolId);
        if (!isCancelled()) {
          setCalendarEntries(
            [...res.data].sort((a, b) =>
              formatCalendarDate(a.date).localeCompare(
                formatCalendarDate(b.date),
              ),
            ),
          );
        }
      } catch (error) {
        logError(error, "loadCalendar");
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              "Failed to load calendar entries. Please try again.",
            ),
          );
          setCalendarEntries([]);
        }
      } finally {
        if (!isCancelled()) setLoading("loadCalendar", false);
      }
    },
    [schoolId, setLoading],
  );

  useEffect(() => {
    let cancelled = false;
    fetchCalendar(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchCalendar]);

  const scheduleTypeById = useMemo(() => {
    const map = new Map<number, ScheduleType>();
    scheduleTypes.forEach((st) => map.set(st.id, st));
    return map;
  }, [scheduleTypes]);

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedScheduleTypeId, setSelectedScheduleTypeId] = useState("");
  const [calendarNote, setCalendarNote] = useState("");

  const addCalendarEntry = async (): Promise<void> => {
    if (!schoolId) return;
    if (!selectedDate) {
      showError("Please select a date.");
      return;
    }

    setLoading("addCalendar", true);
    try {
      await createCalendarEntries(schoolId, {
        date: selectedDate,
        scheduleTypeId: selectedScheduleTypeId
          ? Number(selectedScheduleTypeId)
          : null,
        note: calendarNote.trim() ? calendarNote.trim() : null,
      });

      const scheduleName = selectedScheduleTypeId
        ? scheduleTypeById.get(Number(selectedScheduleTypeId))?.name
        : "No School";
      showSuccess(`Set ${selectedDate} to ${scheduleName}`);

      setSelectedDate("");
      setCalendarNote("");
      await fetchCalendar();
      setCalendarCurrentPage(0);
    } catch (error) {
      logError(error, "addCalendarEntry");
      showError(
        getContextualError(
          error,
          undefined,
          "Failed to add calendar entry. Please try again.",
        ),
      );
    } finally {
      setLoading("addCalendar", false);
    }
  };

  const removeCalendarEntry = async (
    entry: SchoolCalendarResponse,
  ): Promise<void> => {
    if (!schoolId) return;
    if (
      !window.confirm(
        `Remove calendar entry for ${formatCalendarDate(entry.date)}?`,
      )
    ) {
      return;
    }

    setLoading("removeCalendar", true);
    try {
      await deleteCalendarEntry(schoolId, entry.id);
      showSuccess(
        `Removed calendar entry for ${formatCalendarDate(entry.date)}`,
      );
      await fetchCalendar();
      setCalendarCurrentPage(0);
    } catch (error) {
      logError(error, "removeCalendarEntry");
      showError(
        getContextualError(
          error,
          undefined,
          "Failed to remove calendar entry. Please try again.",
        ),
      );
    } finally {
      setLoading("removeCalendar", false);
    }
  };

  const [showCalendarEditModal, setShowCalendarEditModal] = useState(false);
  const [editingCalendarEntry, setEditingCalendarEntry] =
    useState<SchoolCalendarResponse | null>(null);

  const handleEditCalendarEntry = (entry: SchoolCalendarResponse): void => {
    setEditingCalendarEntry(entry);
    setShowCalendarEditModal(true);
  };

  const [calendarCsvText, setCalendarCsvText] = useState("");

  const submitCalendarCsv = async (): Promise<void> => {
    if (!schoolId) return;
    const { errors: parseErrors, entries } = parseCalendarCsv(
      calendarCsvText,
      scheduleTypes,
    );
    if (parseErrors.length > 0) {
      showError(`CSV format issues found:\n\n${parseErrors.join("\n")}`);
      return;
    }

    setLoading("uploadCalendarCsv", true);
    try {
      await createCalendarEntries(schoolId, entries);
      showSuccess(
        `Uploaded ${entries.length} calendar ${
          entries.length === 1 ? "entry" : "entries"
        }`,
      );
      setCalendarCsvText("");
      const fileInput = document.getElementById("calendar-csv-file");
      if (fileInput instanceof HTMLInputElement) {
        fileInput.value = "";
      }
      await fetchCalendar();
      setCalendarCurrentPage(0);
    } catch (error) {
      logError(error, "uploadCalendarCsv");
      showError(
        getContextualError(
          error,
          undefined,
          "Failed to upload calendar CSV. Please try again.",
        ),
      );
    } finally {
      setLoading("uploadCalendarCsv", false);
    }
  };

  const handleCalendarCsvFileSelection = async (
    file: File | undefined,
  ): Promise<void> => {
    if (!file) return;
    try {
      setCalendarCsvText(await readFileAsText(file));
    } catch (error) {
      showError(error instanceof Error ? error.message : String(error));
      const fileInput = document.getElementById("calendar-csv-file");
      if (fileInput instanceof HTMLInputElement) {
        fileInput.value = "";
      }
    }
  };

  const downloadSampleCalendarCsv = (): void => {
    const csvContent = buildSampleCalendarCsv(scheduleTypes);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "calendar_sample.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getFilteredCalendarEntries = (): SchoolCalendarResponse[] => {
    if (!calendarFilter.trim()) return calendarEntries;
    const searchLower = calendarFilter.toLowerCase();
    return calendarEntries.filter((entry) => {
      const dateStr = formatCalendarDate(entry.date);
      const scheduleName = entry.scheduleTypeId
        ? scheduleTypeById.get(entry.scheduleTypeId)?.name || ""
        : "no school";
      return (
        dateStr.toLowerCase().includes(searchLower) ||
        scheduleName.toLowerCase().includes(searchLower)
      );
    });
  };

  const filteredCalendarEntries = getFilteredCalendarEntries();
  const calendarTotalPages = Math.ceil(
    filteredCalendarEntries.length / CALENDAR_PAGE_SIZE,
  );
  const paginatedCalendarEntries = filteredCalendarEntries.slice(
    calendarCurrentPage * CALENDAR_PAGE_SIZE,
    calendarCurrentPage * CALENDAR_PAGE_SIZE + CALENDAR_PAGE_SIZE,
  );

  // Today's schedule preview
  const [todaySchedule, setTodaySchedule] =
    useState<ScheduleTodayResponse | null>(null);

  // Guards against a stale response clobbering `todaySchedule` for the
  // wrong schoolId — same cancelled-flag pattern as fetchScheduleTypes
  // above.
  const fetchTodaySchedule = useCallback(
    async (isCancelled: IsCancelled = () => false) => {
      if (!schoolId) return;
      if (!isCancelled()) setLoading("loadToday", true);
      try {
        const result = await getTodaySchedule(schoolId);
        if (!isCancelled()) setTodaySchedule(result);
      } catch (error) {
        logError(error, "loadTodaySchedule");
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              "Failed to load today's schedule.",
            ),
          );
          setTodaySchedule(null);
        }
      } finally {
        if (!isCancelled()) setLoading("loadToday", false);
      }
    },
    [schoolId, setLoading],
  );

  useEffect(() => {
    let cancelled = false;
    fetchTodaySchedule(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchTodaySchedule]);

  return (
    <>
      <CollapsibleSection
        title="Today's Schedule"
        icon="🕐"
        id="today-schedule-preview"
        defaultExpanded={false}
      >
        <div className="today-schedule-preview">
          {loadingStates.loadToday ? (
            <div className="loading">Loading today's schedule...</div>
          ) : todaySchedule ? (
            <>
              <p>
                <strong>Date:</strong> {todaySchedule.date}
              </p>
              <p>
                <strong>Schedule:</strong>{" "}
                {todaySchedule.scheduleType?.name || "No school today"}
              </p>
              {todaySchedule.currentPeriod && (
                <p>
                  <strong>Current Period:</strong>{" "}
                  {todaySchedule.currentPeriod.name} (
                  {todaySchedule.currentPeriod.startTime}–
                  {todaySchedule.currentPeriod.endTime})
                </p>
              )}
              {todaySchedule.periods.length > 0 && (
                <ul>
                  {todaySchedule.periods.map((p) => (
                    <li key={p.id}>
                      {p.name}: {p.startTime}–{p.endTime}
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <p>No data available.</p>
          )}
          <button
            onClick={() => fetchTodaySchedule()}
            disabled={loadingStates.loadToday}
            className={adminStyles["add-btn"]}
          >
            Refresh
          </button>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Calendar & Schedule Management"
        icon="📅"
        id="calendar-management"
        defaultExpanded={false}
      >
        <div className="calendar-management">
          <div className={styles["calendar-form"]}>
            <h3>Set Calendar Entry</h3>
            <div className="form-row">
              <input
                type="date"
                aria-label="Calendar entry date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className={styles["date-input"]}
              />
              <select
                aria-label="Schedule type for this date"
                value={selectedScheduleTypeId}
                onChange={(e) => setSelectedScheduleTypeId(e.target.value)}
                className={styles["role-select"]}
              >
                <option value="">No School</option>
                {scheduleTypes.map((st) => (
                  <option key={st.id} value={st.id}>
                    {/* Append the A/B designation so the two halves of a
                        split (Block) variant — which share a name — are
                        distinguishable here, matching how the calendar entry
                        list / edit modal surface abDay. */}
                    {st.abDay === "A" || st.abDay === "B"
                      ? `${st.name} (${st.abDay} Day)`
                      : st.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                aria-label="Calendar entry note (optional)"
                placeholder="Note (optional)"
                value={calendarNote}
                onChange={(e) => setCalendarNote(e.target.value)}
                className={adminStyles["teacher-input"]}
              />
              <button
                onClick={addCalendarEntry}
                disabled={loadingStates.addCalendar}
                className={adminStyles["add-btn"]}
              >
                {loadingStates.addCalendar ? "Saving..." : "Set Calendar Entry"}
              </button>
            </div>
          </div>

          <div className={styles["calendar-lists"]}>
            <div className={styles["csv-upload-section"]}>
              <CsvFormatExplainer
                title="Bulk Upload Calendar CSV"
                headingLevel="h3"
                summary="Upload or paste CSV data to set many calendar days at once."
                format="Date,Schedule Type,A/B"
                columns={[
                  {
                    name: "Date",
                    description: "the day to set, as YYYY-MM-DD.",
                  },
                  {
                    name: "Schedule Type",
                    description: "the name of a configured schedule type.",
                  },
                  {
                    name: "A/B",
                    description:
                      "only needed for a split (Block) variant's two halves — leave blank for any other schedule type.",
                  },
                ]}
                note="Only the dates listed in the file are changed; every other date on the calendar is left as-is."
                example={buildSampleCalendarCsv(scheduleTypes)}
              />

              <div className={styles["form-group"]}>
                <label htmlFor="calendar-csv-file">Choose CSV File:</label>
                <input
                  id="calendar-csv-file"
                  type="file"
                  accept=".csv"
                  className={adminStyles["file-input"]}
                  disabled={loadingStates.uploadCalendarCsv}
                  onChange={(e) =>
                    handleCalendarCsvFileSelection(e.target.files?.[0])
                  }
                />
              </div>

              <div className={styles["form-group"]}>
                <label htmlFor="calendar-csv-data">Or Paste CSV Data:</label>
                <textarea
                  id="calendar-csv-data"
                  className={styles["csv-textarea"]}
                  rows={3}
                  value={calendarCsvText}
                  onChange={(e) => setCalendarCsvText(e.target.value)}
                  placeholder="Date,Schedule Type,A/B"
                  disabled={loadingStates.uploadCalendarCsv}
                />
              </div>

              <div className={adminStyles["button-group"]}>
                <button
                  className={adminStyles["add-btn"]}
                  onClick={submitCalendarCsv}
                  disabled={
                    loadingStates.uploadCalendarCsv || !calendarCsvText.trim()
                  }
                >
                  {loadingStates.uploadCalendarCsv
                    ? "Uploading..."
                    : "Upload Calendar CSV"}
                </button>
                <button
                  className="action-button"
                  onClick={downloadSampleCalendarCsv}
                >
                  Download Sample CSV
                </button>
              </div>
            </div>

            <div className="calendar-entries">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <h3>Calendar ({filteredCalendarEntries.length})</h3>
                <SearchFilter
                  value={calendarFilter}
                  onChange={setCalendarFilter}
                  placeholder="Search by date or schedule..."
                />
              </div>
              {loadingStates.loadCalendar ? (
                <div className="loading">Loading calendar entries...</div>
              ) : calendarEntries.length === 0 ? (
                <p>No calendar entries yet. Set one above.</p>
              ) : filteredCalendarEntries.length === 0 ? (
                <p>No calendar entries match your search.</p>
              ) : (
                <>
                  <div className={styles["entries-list"]}>
                    {paginatedCalendarEntries.map((entry) => {
                      const entryScheduleType = entry.scheduleTypeId
                        ? scheduleTypeById.get(entry.scheduleTypeId)
                        : null;
                      const entryAbDay =
                        entryScheduleType &&
                        (entryScheduleType.abDay === "A" ||
                          entryScheduleType.abDay === "B")
                          ? entryScheduleType.abDay
                          : null;
                      return (
                        <div key={entry.id} className={styles["calendar-entry"]}>
                          <div className={styles["entry-info"]}>
                            <strong>{formatCalendarDate(entry.date)}</strong>
                            <span className={styles["schedule-badge"]}>
                              {entry.scheduleTypeId
                                ? entryScheduleType?.name ||
                                  `Schedule type ${entry.scheduleTypeId}`
                                : "No School"}
                            </span>
                            {entryAbDay && (
                              <span
                                className={`${styles["ab-badge"]} ${
                                  styles[`ab-${entryAbDay.toLowerCase()}`]
                                }`}
                              >
                                {entryAbDay}
                              </span>
                            )}
                            {entry.note && (
                              <span className="entry-note">{entry.note}</span>
                            )}
                          </div>
                          <div className={styles["entry-actions"]}>
                            <button
                              onClick={() => handleEditCalendarEntry(entry)}
                              className={adminStyles["btn-edit"]}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => removeCalendarEntry(entry)}
                              disabled={loadingStates.removeCalendar}
                              className={adminStyles["remove-btn"]}
                            >
                              {loadingStates.removeCalendar
                                ? "Removing..."
                                : "Remove"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <PaginationControls
                    currentPage={calendarCurrentPage}
                    totalPages={calendarTotalPages}
                    onPageChange={setCalendarCurrentPage}
                    totalItems={filteredCalendarEntries.length}
                    pageSize={CALENDAR_PAGE_SIZE}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Schedule Types Management"
        icon="📋"
        id="schedule-variants"
        defaultExpanded={false}
      >
        <div className={styles["schedule-variants-management"]}>
          <div className={styles["section-info"]}>
            {schoolConfig && (
              <p>
                <strong>School Type:</strong> {schoolConfig.schoolType} |
                <strong> Total Periods:</strong> {schoolConfig.totalPeriods}
              </p>
            )}
            <p>
              Create and manage different schedule types for special events,
              testing days, or alternate schedules. A schedule type with A/B day
              periods is stored as two schedule types sharing one name,
              distinguished by their A/B day; assign specific dates to either
              one from the Calendar section above (or bulk-assign many at once
              via CSV upload).
            </p>
          </div>

          <div className={styles["variants-header"]}>
            <h3>Schedule Types ({variantGroups.length})</h3>
            <button
              onClick={handleCreateVariant}
              disabled={loadingStates.loadScheduleTypes}
              className={styles["create-variant-btn"]}
            >
              + Create New Schedule Type
            </button>
          </div>

          {loadingStates.loadScheduleTypes ? (
            <div className="loading">Loading schedule types...</div>
          ) : variantGroups.length === 0 ? (
            <div className={styles["empty-variants"]}>
              <p>No schedule types created yet.</p>
              <p>Create one above.</p>
            </div>
          ) : (
            <div className={styles["variants-list"]}>
              {variantGroups.map((variant) => {
                const isSplit = isSplitVariant(variant);
                return (
                  <div key={variant.variantName} className={styles["variant-card"]}>
                    <div className={styles["variant-info"]}>
                      <h4>{variant.variantName}</h4>
                      <div className={styles["variant-stats"]}>
                        <span>
                          {isSplit ? "A Day + B Day" : "Single schedule"}
                        </span>
                      </div>
                    </div>
                    <div className={styles["variant-actions"]}>
                      <button
                        onClick={() => handleEditVariant(variant)}
                        className={adminStyles["btn-edit"]}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDuplicateVariant(variant)}
                        className={styles["btn-duplicate"]}
                      >
                        Duplicate
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CollapsibleSection>

      <ScheduleVariantModal
        isOpen={showVariantModal}
        onClose={() => setShowVariantModal(false)}
        onVariantSaved={handleVariantSaved}
        schoolConfig={schoolConfig}
        schoolId={schoolId}
        mode={variantModalMode}
        existingVariant={selectedVariant}
      />

      <CalendarEntryEditModal
        isOpen={showCalendarEditModal}
        onClose={() => setShowCalendarEditModal(false)}
        onEntrySaved={fetchCalendar}
        schoolId={schoolId}
        entry={editingCalendarEntry}
        variantGroups={variantGroups}
      />
    </>
  );
};

export default ScheduleConfigPanel;
